// src/components/common/MaterialCard.jsx
import React, { useState } from 'react';
import { 
  FileText, Download, Eye, Calendar, User, FileImage, File, 
  ExternalLink, Maximize2, FileDown, Image as ImageIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { publicAPI } from '../../services/api';

const MaterialCard = ({ material, onClick, type = 'default' }) => {
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const styles = {
    badge: 'bg-gray-100 text-gray-700',
    icon: 'bg-gray-50 text-gray-600',
    accent: 'from-gray-400 to-gray-600'
  };

  const isImage = publicAPI.isImage(material);
  const canPreview = publicAPI.canPreview(material);

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!material._id) return;
    
    setDownloading(true);
    
    try {
      const downloadUrl = publicAPI.getDownloadUrl(material._id);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', material.file?.originalName || material.title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      await publicAPI.trackDownload(material._id);
      
      // Show download notification
      if (window.addDownloadNotification) {
        window.addDownloadNotification(material);
      }
    } catch (err) {
      console.error(err);
      alert('Download failed.');
    } finally {
      setTimeout(() => setDownloading(false), 2000);
    }
  };

  const handlePreview = async (e) => {
    e.stopPropagation();
    if (!material._id) return;
    
    if (!canPreview) {
      alert('Preview not available for this file type. Please download instead.');
      return;
    }
    
    setPreviewing(true);
    
    try {
      if (window.openFilePreview) {
        window.openFilePreview(material);
      } else if (window.openImagePreview) {
        window.openImagePreview(material);
      } else {
        const previewUrl = publicAPI.getPreviewUrl(material._id);
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
      }
      
      await publicAPI.trackDownload(material._id);
      
      // Show preview notification
      if (window.addPreviewNotification) {
        window.addPreviewNotification(material);
      }
      
    } catch (err) {
      console.error(err);
      alert('Preview failed.');
    } finally {
      setTimeout(() => setPreviewing(false), 1000);
    }
  };

  const getFileIcon = () => {
    if (isImage) return <FileImage className="w-6 h-6" />;
    if (material.file?.format?.toLowerCase() === 'pdf') return <FileText className="w-6 h-6" />;
    return <File className="w-6 h-6" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  return (
    <motion.div
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
      whileTap={{ scale: 0.98 }}
      className="group relative"
    >
      <div 
        className="relative bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 cursor-pointer"
        onClick={onClick}
      >
        <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${styles.accent}`} />
        
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className={`p-3 rounded-lg ${styles.icon} group-hover:scale-110 transition-transform duration-300`}>
              {getFileIcon()}
            </div>
            <div className="flex gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles.badge} flex items-center gap-1`}>
                {material.file?.format || 'FILE'}
                {isImage && ' 📷'}
              </span>
              <button
                onClick={handlePreview}
                disabled={previewing || !canPreview}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                  canPreview 
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                    : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                }`}
                title={canPreview ? 'Preview file' : 'Preview not available'}
              >
                <Eye className="w-3 h-3" /> 
                {previewing ? 'Opening...' : 'Preview'}
              </button>
            </div>
          </div>
          
          <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {material.title}
          </h3>
          
          {/* Image thumbnail preview */}
          {isImage && (
            <div className="mb-4 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
              <div 
                className="w-full h-32 relative group cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.openImagePreview) {
                    window.openImagePreview(material);
                  }
                }}
              >
                <img 
                  src={publicAPI.getPreviewUrl(material._id)}
                  alt={material.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                        <rect width="100" height="100" fill="#f3f4f6"/>
                        <text x="50" y="50" font-family="Arial" font-size="14" fill="#9ca3af" text-anchor="middle" dy=".3em">${material.file?.format?.toUpperCase() || 'IMAGE'}</text>
                      </svg>
                    `)}`;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="bg-black/50 text-white p-2 rounded-full">
                    <Maximize2 className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {material.teacher?.showAvatarPublicly !== false && material.teacher?.profileImage?.url ? (
                <div className="w-5 h-5 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                  <img 
                    src={publicAPI.getFullImageUrl(material.teacher.profileImage.url)}
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <User className="w-4 h-4" />
              )}
              <span className="truncate font-medium">{material.teacher?.fullName || "Unknown Teacher"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{new Date(material.createdAt).toLocaleDateString()}</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-500">{formatFileSize(material.file?.size)}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <Download className="w-4 h-4" />
                <span className="font-semibold">{material.downloads || 0}</span>
              </div>
              {isImage && (
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Eye className="w-4 h-4" />
                  <span className="font-semibold">{material.views || 0}</span>
                </div>
              )}
            </div>
            
            <button
              onClick={handleDownload}
              disabled={downloading}
              className={`p-2 rounded-lg ${styles.icon} hover:bg-blue-100 hover:text-blue-600 transition-colors flex items-center gap-1 disabled:opacity-50`}
              title="Download"
            >
              <Download className="w-4 h-4" />
              {downloading && '...'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MaterialCard;